/*
 * The markdown editor.
 *
 * A textarea with a toolbar, not a rich-text surface. What is stored in
 * kb.ArticleRevision.Content is markdown, and an editor that hides that is an
 * editor that will eventually disagree with it - a WYSIWYG that round-trips
 * markdown imperfectly silently rewrites the author's text on every save.
 *
 * The preview is the useful part: it renders through exactly the same pipeline the
 * public article page uses, and it asks the server to resolve the [[kb:...]] tokens
 * so an author can see, while writing, that a link points at something that does not
 * exist or is not published yet.
 *
 * Images
 * ------
 * Three ways in - paste, drop, and the file picker - all landing on one code path,
 * because the interesting work is identical and only the way the File is obtained
 * differs. What that path does, and why:
 *
 *   - It checks the bytes before uploading, using the same shared inspection the
 *     server enforces. Advisory only: the browser is not trusted, it is being
 *     helpful. Refusing a 6 MB paste in a hundred milliseconds beats refusing it
 *     after a minute of upload.
 *   - It shows progress. fetch cannot report upload progress, which is why the
 *     transport in adminApi.ts is XMLHttpRequest.
 *   - It inserts the name the SERVER returned, never a name guessed here. Only the
 *     server can see what is already attached, so only the server can say which
 *     pasted-<n> was free.
 *   - It leaves the caret inside the alt-text brackets. Alt text is an
 *     accessibility requirement, and the moment after an image lands is the only
 *     moment the author is thinking about what it shows.
 *
 * An image belongs to an article, so there is nothing to attach it to until the
 * article has been saved once and has an ArticleId. That case is explained on
 * screen rather than being allowed to fail as a request to /articles//images.
 *
 * The preview shows attached images through blob: URLs. It cannot use the admin
 * endpoint directly: requireAdmin covers the whole /api/admin router and an <img>
 * cannot send an Authorization header, so the bytes are fetched with the token and
 * handed to the element as an object URL. Every one of them is revoked - on
 * replacement, and on unmount - or the article's images stay in memory for as long
 * as the tab is open.
 *
 * Not only articles (S42, 2026-10-12)
 * -----------------------------------
 * The release-note editor is this editor too. What used to hang off one
 * `articleId` prop is now three separate things, because a release note needs
 * them apart:
 *
 *   imageOwner        where images go and come from, the ceiling and its message
 *                     (imageOwner.ts). `articleId` stays as the article shorthand
 *                     for it, so every article call site is unchanged; an
 *                     explicit owner wins.
 *   excludeArticleId  which article the link picker leaves out - an article
 *                     cannot link to itself. Defaults to `articleId`; a release
 *                     note excludes nothing.
 *   noun              what the sentences call the thing being written. Defaults
 *                     to 'article', and every article string is unchanged.
 *
 * And `allowVideo` (default true): release notes take no video embeds by the
 * owner's answer, so their editor hides "Insert video" and tells the preview
 * not to build embeds either - the token then reads as the literal text a reader
 * of the published note would see.
 *
 * Its words come from the chosen language's dictionary, t.admin.editor (S48).
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ArticleImage,
  ArticleImageAudit,
  ArticleImageContentType,
  ArticleImageUnsafeReason,
  ArticleLinkResolution,
  AttachmentLinkResolution,
} from '@accotool-kb/shared';
import {
  ARTICLE_IMAGE_ACCEPT_ATTRIBUTE,
  ARTICLE_IMAGE_ALT_CARET_OFFSET,
  ARTICLE_IMAGE_MAX_BYTES,
  auditArticleImages,
  buildArticleImageMarkdown,
  collectArticleImageFileNames,
  countArticleImageReferences,
  inspectArticleImageBytes,
  nextPastedImageFileName,
  toArticleImageFileName,
} from '@accotool-kb/shared';
import { useAdminApi } from '../../auth/AuthProvider.js';
import { useDebounced } from '../../hooks/useAsync.js';
import { MarkdownPreview } from '../../components/Markdown.js';
import { ApiError } from '../../api/client.js';
import type { AdminApi } from '../../api/adminApi.js';
import { ArticlePickerDialog } from './ArticlePickerDialog.js';
import { VideoInsertDialog } from './VideoInsertDialog.js';
import { syncPreviewHeight } from './syncPreviewHeight.js';
import { ArticleLinkIcon, ImageIcon, VideoIcon } from './toolbarIcons.js';
import { useImagePreviews } from './useArticleImagePreviews.js';
import { articleImageOwner, type ImageOwner } from './imageOwner.js';
import { adminAttachmentHref, parseAdminAttachmentHref } from './attachmentFiles.js';
import { useAttachmentDownload } from '../../hooks/useAttachmentDownload.js';
import { ErrorBanner } from '../../components/ui.js';
import type { Messages } from '../../i18n/en.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';

/** The editor's words: t.admin.editor. */
type EditorWords = Messages['admin']['editor'];

type ViewMode = 'split' | 'write' | 'preview';

/** What the editor exposes to the page around it. */
export interface MarkdownEditorHandle {
  /** Inserts text at the caret, or at the end if the textarea is not focused. */
  insertAtCaret: (text: string) => void;
  /**
   * Inserts `![](file-name.png)` and leaves the caret between the brackets, which
   * is where the alt text goes. Used by the paste handler and by the image list.
   */
  insertImageReference: (fileName: string) => void;
}

export interface MarkdownEditorProps {
  value: string;
  onChange: (next: string) => void;
  /**
   * The article shorthand: the image owner is this article, and the article
   * picker leaves it out so an article cannot link to itself. Absent on an
   * article that has not been saved yet.
   */
  articleId?: string;
  /**
   * Whose images these are, when they are not an article's - a release note's.
   * Wins over `articleId` for everything image-related. Neither given means
   * nothing has been saved yet, and an image is explained rather than uploaded.
   */
  imageOwner?: ImageOwner;
  /** Left out of the article picker. Defaults to `articleId`. */
  excludeArticleId?: string;
  /**
   * What the sentences call the thing being written, lower case: 'article' (the
   * default) or 'release note'. Defaults to the image owner's noun when one is
   * given.
   */
  noun?: string;
  /**
   * Whether video embeds are offered and previewed (default true). False hides
   * "Insert video" and renders a video token as the literal text it would be on
   * the published page - release notes take no videos.
   */
  allowVideo?: boolean;
  /**
   * Whether [[attachment:...]] tokens are resolved and previewed as links
   * (default true, S47). False for a release note, which has no attachments:
   * the token then reads as the literal text its readers would see.
   */
  allowAttachments?: boolean;
  disabled?: boolean;
  /**
   * The article's attachments. Drives the preview, the reference audit and the
   * pasted-<n> counter. Absent on an article that has not been saved yet, which is
   * the same thing as having none.
   */
  images?: readonly ArticleImage[];
  /** Called with the whole new list after an upload, for the page to hold. */
  onImagesChanged?: (next: ArticleImage[]) => void;
}

/** One upload in flight, or one that failed and is waiting to be read. */
interface UploadState {
  id: number;
  fileName: string;
  byteLength: number;
  /** 0 to 1. */
  progress: number;
  status: 'uploading' | 'failed';
  message?: string;
  /**
   * Kept so a refused name can be retried as a replacement without the author
   * having to find the file again. A pasted image cannot be found again at all -
   * the clipboard has moved on - so this is the only copy of it we will ever have.
   */
  file?: File;
  canReplace?: boolean;
}

let uploadSequence = 0;

export const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(
  function MarkdownEditor(
    {
      value,
      onChange,
      articleId,
      imageOwner,
      excludeArticleId,
      noun: nounProp,
      allowVideo = true,
      allowAttachments = true,
      disabled,
      images = [],
      onImagesChanged,
    },
    ref,
  ): JSX.Element {
    const t = useMessages();
    const words = t.admin.editor;
    const api = useAdminApi();

    /*
     * The image owner, from the explicit adapter or the article shorthand. Null
     * means nothing has been saved yet: there is nothing to attach an image to.
     * Memoised on what it is built from, though nothing downstream depends on
     * its identity - the preview cache keys on `owner.key`.
     */
    const owner = useMemo<ImageOwner | null>(
      () => imageOwner ?? (articleId ? articleImageOwner(api, articleId) : null),
      [imageOwner, api, articleId],
    );
    const noun = nounProp ?? imageOwner?.noun ?? 'article';
    const pickerExclusion = excludeArticleId ?? articleId;
    const textarea = useRef<HTMLTextAreaElement>(null);
    const filePicker = useRef<HTMLInputElement>(null);
    // The split wrapper, so the two panes' heights can be coordinated. The
    // preview div belongs to MarkdownPreview, whose job is rendering rather
    // than layout - querying it from the element that owns the split keeps the
    // coupling where the layout decision already lives.
    const layout = useRef<HTMLDivElement>(null);
    const [mode, setMode] = useState<ViewMode>('split');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [videoOpen, setVideoOpen] = useState(false);
    const [linkTargets, setLinkTargets] = useState<ArticleLinkResolution[]>([]);
    const [attachmentLinks, setAttachmentLinks] = useState<AttachmentLinkResolution[]>([]);
    const attachmentDownload = useAttachmentDownload();
    const [uploads, setUploads] = useState<UploadState[]>([]);
    const [dragging, setDragging] = useState(false);
    const [needsSaving, setNeedsSaving] = useState(false);

    /*
     * The text and the attachments as they are RIGHT NOW, for the async paths.
     *
     * An upload takes seconds, and the author keeps typing during them. A closure
     * that captured `value` when the paste happened would insert the reference into
     * the text as it was before they typed, throwing away everything since. The
     * same applies to `images`: two pastes in quick succession must not both be
     * told that pasted-1 is free.
     */
    const valueRef = useRef(value);
    valueRef.current = value;
    const imagesRef = useRef<readonly ArticleImage[]>(images);
    imagesRef.current = images;

    // Only the debounced text is sent for token resolution: one request per pause in
    // typing rather than one per keystroke.
    const debouncedValue = useDebounced(value, 500);

    useEffect(() => {
      if (mode === 'write') return;
      // Attachment tokens (S47) ride on the same request: one per pause in typing
      // for both kinds, and none at all for text that has neither.
      const wantsAttachments = allowAttachments && debouncedValue.includes('[[attachment:');
      if (!debouncedValue.includes('[[kb:') && !wantsAttachments) {
        setLinkTargets([]);
        setAttachmentLinks([]);
        return;
      }

      let cancelled = false;
      api
        .previewLinks(debouncedValue)
        .then((resolved) => {
          if (cancelled) return;
          setLinkTargets(resolved.linkTargets);
          setAttachmentLinks(allowAttachments ? resolved.attachmentLinks : []);
        })
        .catch(() => {
          // A failed resolution is not worth interrupting writing for: the preview
          // simply shows the tokens as unresolved.
          if (cancelled) return;
          setLinkTargets([]);
          setAttachmentLinks([]);
        });

      return () => {
        cancelled = true;
      };
    }, [api, debouncedValue, mode, allowAttachments]);

    /*
     * A click on an attachment link in the preview downloads the file, through
     * the admin API's link: an administrator may have any file, listed on this
     * article or not, and the preview is where an author checks the link works.
     */
    const { download } = attachmentDownload;
    const handlePreviewLinkClick = useCallback(
      (href: string): boolean => {
        const attachmentId = parseAdminAttachmentHref(href);
        if (!attachmentId) return false;
        void download(() => api.attachmentDownloadLink(attachmentId));
        return true;
      },
      [api, download],
    );

    const attachedNames = useMemo(() => images.map((image) => image.fileName), [images]);

    /*
     * What is wrong with the images, recomputed from the text as it is typed.
     *
     * Not debounced, and not in the preview pane. Every one of these is something
     * only the author can fix, and the one that matters most - an empty alt
     * attribute - is invisible in a rendered preview by definition.
     */
    const audit = useMemo(() => auditArticleImages(value, attachedNames), [value, attachedNames]);

    /*
     * The attachments the text actually uses, from the settled text.
     *
     * The preview fetches these and only these. Fetching every attachment would
     * pull down the ones the author has already stopped referencing, and at 5 MB
     * apiece that is a real cost for no benefit. Debounced, so a half-typed name
     * does not start a request.
     */
    const referenced = useMemo(
      () =>
        new Set(
          collectArticleImageFileNames(
            auditArticleImages(debouncedValue, attachedNames).references,
          ),
        ),
      [debouncedValue, attachedNames],
    );

    const previewable = useMemo(
      () => images.filter((image) => referenced.has(image.fileName)),
      [images, referenced],
    );

    const previews = useImagePreviews(owner, previewable, mode !== 'write');

    const buildImageSrc = useCallback(
      (fileName: string): string => {
        const objectUrl = previews[fileName];
        if (objectUrl) return objectUrl;

        // No object URL: the name is not attached, or its bytes are still on the
        // way. Point at the endpoint regardless - it answers 404 or 401, and
        // Markdown turns that into a visible placeholder carrying the alt text,
        // which is exactly what the author needs to see. With no owner there is
        // nothing to point at, so the bare name is left to fail on its own.
        return owner ? owner.path(fileName) : fileName;
      },
      [previews, owner],
    );

    /**
     * Wraps or replaces the selection, then restores a sensible caret position - the
     * difference between a toolbar that helps and one that makes you find your place
     * again after every click.
     */
    const surround = useCallback(
      (before: string, after = '', placeholder = '') => {
        const element = textarea.current;
        if (!element) return;

        const start = element.selectionStart;
        const end = element.selectionEnd;
        const scrollTop = element.scrollTop;
        const selected = value.slice(start, end) || placeholder;
        const next = `${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`;

        onChange(next);

        const caretStart = start + before.length;
        restoreCaret(element, caretStart, caretStart + selected.length, scrollTop);
      },
      [value, onChange],
    );

    const prefixLines = useCallback(
      (prefix: string | ((index: number) => string)) => {
        const element = textarea.current;
        if (!element) return;

        const start = value.lastIndexOf('\n', element.selectionStart - 1) + 1;
        const rawEnd = value.indexOf('\n', element.selectionEnd);
        const end = rawEnd === -1 ? value.length : rawEnd;
        const scrollTop = element.scrollTop;

        const block = value.slice(start, end) || '';
        const updated = block
          .split('\n')
          .map((line, index) => `${typeof prefix === 'function' ? prefix(index) : prefix}${line}`)
          .join('\n');

        onChange(`${value.slice(0, start)}${updated}${value.slice(end)}`);
        restoreCaret(element, start, start + updated.length, scrollTop);
      },
      [value, onChange],
    );

    /*
     * Inserts at the caret, reading the text from the ref rather than the prop.
     *
     * That is what makes it safe to call when an upload finishes: the author may
     * have typed a paragraph while it was in flight, and the prop captured at paste
     * time no longer describes the document.
     */
    /*
     * Drag the textarea's resize handle and the preview follows.
     *
     * ResizeObserver rather than a drag listener: the native handle emits no
     * events of its own, and observing the box also catches a height changed by
     * anything else. It fires once on observe, which is what re-applies a height
     * after a trip through Write-only mode has re-created the preview element.
     *
     * Everything about what to set lives in syncPreviewHeight, including the
     * no-drag case, which writes nothing at all.
     */
    useEffect(() => {
      const element = textarea.current;
      // Not in split view there is no second pane to match, and jsdom has no
      // ResizeObserver - a missing one must not take the editor down with it.
      if (mode !== 'split' || !element || typeof ResizeObserver === 'undefined') return;

      const preview = () => layout.current?.querySelector<HTMLElement>('.preview') ?? null;
      const sync = () => syncPreviewHeight(element, preview());

      const observer = new ResizeObserver(sync);
      observer.observe(element);

      /*
       * And on pointer movement while a button is held, which is a drag in
       * progress.
       *
       * The observer alone is not enough, and not only because its callback is
       * frame-tied: on the FIRST downward drag the textarea is still
       * flex-stretched, so the box does not change size at all and the observer
       * has nothing to report. Syncing from the pointer breaks that deadlock -
       * the first move hands the height to syncPreviewHeight, which turns
       * flex-grow off, and from then on both signals agree.
       *
       * pointerup catches the end of the gesture, including a drag that ended
       * outside the element.
       */
      const syncWhileDragging = (event: PointerEvent) => {
        if (event.buttons !== 0) sync();
      };
      element.addEventListener('pointermove', syncWhileDragging);
      element.addEventListener('pointerup', sync);
      window.addEventListener('pointerup', sync);

      return () => {
        observer.disconnect();
        element.removeEventListener('pointermove', syncWhileDragging);
        element.removeEventListener('pointerup', sync);
        window.removeEventListener('pointerup', sync);
      };
    }, [mode]);

    const insertAtCaret = useCallback(
      (text: string) => {
        const current = valueRef.current;
        const element = textarea.current;
        if (!element) {
          onChange(`${current}${text}`);
          return;
        }

        const start = element.selectionStart;
        const end = element.selectionEnd;
        const scrollTop = element.scrollTop;
        onChange(`${current.slice(0, start)}${text}${current.slice(end)}`);

        const caret = start + text.length;
        restoreCaret(element, caret, caret, scrollTop);
      },
      [onChange],
    );

    /**
     * Inserts an image reference and parks the caret between the alt brackets.
     *
     * The offset is ARTICLE_IMAGE_ALT_CARET_OFFSET from the shared module rather
     * than a literal 2, so it cannot drift away from the markdown that module
     * builds.
     */
    const insertImageReference = useCallback(
      (fileName: string) => {
        const markdown = buildArticleImageMarkdown({ fileName });
        const current = valueRef.current;
        const element = textarea.current;

        if (!element) {
          onChange(`${current}${markdown}`);
          return;
        }

        const start = element.selectionStart;
        const end = element.selectionEnd;
        const scrollTop = element.scrollTop;
        onChange(`${current.slice(0, start)}${markdown}${current.slice(end)}`);

        const caret = start + ARTICLE_IMAGE_ALT_CARET_OFFSET;
        restoreCaret(element, caret, caret, scrollTop);
      },
      [onChange],
    );

    useImperativeHandle(
      ref,
      () => ({ insertAtCaret, insertImageReference }),
      [insertAtCaret, insertImageReference],
    );

    const dismissUpload = useCallback((id: number) => {
      setUploads((previous) => previous.filter((upload) => upload.id !== id));
    }, []);

    /**
     * Uploads one file under one name, and reports what happened.
     *
     * The reference is inserted only when the text does not already contain one for
     * that name. Replacing an image the article already shows should not litter the
     * markdown with a second copy of the same reference.
     */
    const runUpload = useCallback(
      async (file: File, fileName: string, replace: boolean): Promise<void> => {
        if (!owner) {
          setNeedsSaving(true);
          return;
        }

        const id = ++uploadSequence;
        setUploads((previous) => [
          ...previous,
          { id, fileName, byteLength: file.size, progress: 0, status: 'uploading' },
        ]);

        try {
          const response = await owner.upload(file, {
            fileName,
            replace,
            onProgress: (fraction) =>
              setUploads((previous) =>
                previous.map((upload) =>
                  upload.id === id ? { ...upload, progress: fraction } : upload,
                ),
              ),
          });

          setUploads((previous) => previous.filter((upload) => upload.id !== id));

          // One row per name - the primary key is (ArticleId, FileName) - so a
          // replacement takes the place of the old entry rather than joining it.
          const stored = response.image;
          const next = [
            ...imagesRef.current.filter((image) => image.fileName !== stored.fileName),
            stored,
          ].sort((left, right) => left.fileName.localeCompare(right.fileName));
          onImagesChanged?.(next);

          if (countArticleImageReferences(valueRef.current, stored.fileName) === 0) {
            insertImageReference(stored.fileName);
          }
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            setUploads((previous) => previous.filter((upload) => upload.id !== id));
            return;
          }

          setUploads((previous) =>
            previous.map((upload) =>
              upload.id === id
                ? {
                    ...upload,
                    status: 'failed',
                    progress: 0,
                    message: describeUploadFailure(error, words.editor),
                    file,
                    canReplace: isNameTaken(error),
                  }
                : upload,
            ),
          );
        }
      },
      [owner, insertImageReference, onImagesChanged, words],
    );

    /**
     * The one path every image takes in, whether it was pasted, dropped or picked.
     *
     * Sequential on purpose. Three screenshots dropped together are three requests
     * one after another, so each one sees the names the previous one took, and the
     * progress list reads as a queue rather than three bars racing.
     */
    const uploadFiles = useCallback(
      async (files: readonly File[]): Promise<void> => {
        if (disabled) return;

        if (!owner) {
          setNeedsSaving(true);
          return;
        }

        // Extended as the loop goes, rather than re-read from the ref: React has not
        // necessarily re-rendered between two iterations, and pasted-1 must not be
        // handed out twice.
        const taken = new Set(imagesRef.current.map((image) => image.fileName));

        for (const file of files) {
          const inspection = await inspectFileForUpload(file, t);

          if (!inspection.ok) {
            setUploads((previous) => [
              ...previous,
              {
                id: ++uploadSequence,
                fileName: file.name || words.editor.pastedImage,
                byteLength: file.size,
                progress: 0,
                status: 'failed',
                message: inspection.message,
              },
            ]);
            continue;
          }

          const fileName = chooseFileName(file, inspection.contentType, taken);

          // A NEW name is one more image, and the owner may carry twenty - its own
          // ceiling, in its own words: an article's and a release note's are
          // separate constants and separate triggers. Refused here so the author
          // hears it before the bytes leave the browser; the server and the
          // database say the same to anyone else. A name already attached is a
          // replacement and adds nothing, so it is let through.
          if (!taken.has(fileName) && taken.size >= owner.maxImages) {
            setUploads((previous) => [
              ...previous,
              {
                id: ++uploadSequence,
                fileName,
                byteLength: file.size,
                progress: 0,
                status: 'failed',
                message: owner.describeCountLimit(t.admin.rules),
              },
            ]);
            continue;
          }

          taken.add(fileName);
          await runUpload(file, fileName, false);
        }
      },
      [owner, disabled, runUpload, t, words],
    );

    const retryAsReplacement = useCallback(
      (upload: UploadState) => {
        const file = upload.file;
        if (!file) return;
        dismissUpload(upload.id);
        void runUpload(file, upload.fileName, true);
      },
      [dismissUpload, runUpload],
    );

    /*
     * Paste.
     *
     * The Files are pulled out of the event synchronously, before any await: the
     * DataTransfer behind clipboardData is only valid for the duration of the
     * handler, and reading it after an await gets an empty list.
     *
     * preventDefault only when an image was actually found, so a normal text paste
     * is left completely alone.
     */
    const onPaste = useCallback(
      (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const files = imageFilesFromClipboard(event.clipboardData);
        if (files.length === 0) return;

        event.preventDefault();
        void uploadFiles(files);
      },
      [uploadFiles],
    );

    /*
     * Drop, which is the same code path with the File obtained differently - the
     * whole reason it is worth having.
     *
     * Every dropped file is offered to the inspection, including a .bmp or an .svg.
     * Being told "SVG is not accepted, because an SVG can carry scripts" is more use
     * than a drop that appears to do nothing.
     */
    const onDrop = useCallback(
      (event: React.DragEvent<HTMLTextAreaElement>) => {
        const files = Array.from(event.dataTransfer.files);
        setDragging(false);
        // No files means dragged text, which the textarea handles perfectly well by
        // itself.
        if (files.length === 0) return;

        event.preventDefault();
        void uploadFiles(files);
      },
      [uploadFiles],
    );

    const onDragOver = useCallback((event: React.DragEvent<HTMLTextAreaElement>) => {
      if (!event.dataTransfer.types.includes('Files')) return;
      // Without this the browser refuses the drop entirely.
      event.preventDefault();
      setDragging(true);
    }, []);

    const onFilesPicked = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        // Cleared so that picking the same file twice fires a change event both
        // times, which it otherwise does not.
        event.target.value = '';
        if (files.length > 0) void uploadFiles(files);
      },
      [uploadFiles],
    );

    // Ctrl/Cmd+B and +I, because anyone who writes markdown all day expects them.
    const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      const key = event.key.toLowerCase();
      if (key === 'b') {
        event.preventDefault();
        surround('**', '**', words.inserted.boldText);
      } else if (key === 'i') {
        event.preventDefault();
        surround('_', '_', words.inserted.italicText);
      } else if (key === 'k') {
        event.preventDefault();
        surround('[', '](https://)', words.inserted.linkText);
      }
    };

    const imageProblems =
      audit.missing.length + audit.malformed.length + audit.unsafe.length + audit.missingAlt.length;

    return (
      <>
        <div className="editor-toolbar" role="toolbar" aria-label={words.toolbar.label}>
          <ToolbarButton label={words.toolbar.bold} onClick={() => surround('**', '**', words.inserted.boldText)} disabled={disabled}>
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton label={words.toolbar.italic} onClick={() => surround('_', '_', words.inserted.italicText)} disabled={disabled}>
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton label={words.toolbar.inlineCode} onClick={() => surround('`', '`', words.inserted.code)} disabled={disabled}>
            <code>{'</>'}</code>
          </ToolbarButton>

          <span style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} aria-hidden="true" />

          <ToolbarButton label={words.toolbar.heading2} onClick={() => prefixLines('## ')} disabled={disabled}>
            H2
          </ToolbarButton>
          <ToolbarButton label={words.toolbar.heading3} onClick={() => prefixLines('### ')} disabled={disabled}>
            H3
          </ToolbarButton>
          <ToolbarButton label={words.toolbar.bulletedList} onClick={() => prefixLines('- ')} disabled={disabled}>
            •
          </ToolbarButton>
          <ToolbarButton
            label={words.toolbar.numberedList}
            onClick={() => prefixLines((index) => `${index + 1}. `)}
            disabled={disabled}
          >
            1.
          </ToolbarButton>
          <ToolbarButton label={words.toolbar.quote} onClick={() => prefixLines('> ')} disabled={disabled}>
            ❝
          </ToolbarButton>
          <ToolbarButton
            label={words.toolbar.codeBlock}
            onClick={() => surround('```\n', '\n```', words.inserted.code)}
            disabled={disabled}
          >
            {'{ }'}
          </ToolbarButton>
          <ToolbarButton
            label={words.toolbar.table}
            onClick={() => {
              const { tableColumn: column, tableValue: value } = words.inserted;
              insertAtCaret(`\n| ${column} | ${column} |\n| --- | --- |\n| ${value} | ${value} |\n`);
            }}
            disabled={disabled}
          >
            ▦
          </ToolbarButton>
          <ToolbarButton
            label={words.toolbar.externalLink}
            onClick={() => surround('[', '](https://)', words.inserted.linkText)}
            disabled={disabled}
          >
            🔗
          </ToolbarButton>

          <span style={{ width: 1, background: 'var(--border)', margin: '0 4px' }} aria-hidden="true" />

          {/*
            "Another" only where the text is itself an article; a release note
            links to an article, not to another one of anything.
          */}
          <ToolbarButton
            label={words.toolbar.articleLink(noun)}
            onClick={() => setPickerOpen(true)}
            disabled={disabled}
          >
            <ArticleLinkIcon />
          </ToolbarButton>

          {/*
            Left enabled without an owner on purpose: clicking it explains that the
            article has to be saved first, which is more use than a button that is
            greyed out for a reason nobody can read.
          */}
          <ToolbarButton
            label={words.toolbar.insertImage}
            title={words.toolbar.insertImageHint}
            onClick={() => (owner ? filePicker.current?.click() : setNeedsSaving(true))}
            disabled={disabled}
          >
            <ImageIcon />
          </ToolbarButton>

          {/*
            Unlike the image button, this needs no saved article and no upload: a
            video is a link, not an attachment. The dialog validates the link
            against the same allowlist the renderer uses before it inserts
            anything. Absent, not disabled, where videos are not taken at all:
            a release note never embeds one, so there is nothing to explain.
          */}
          {allowVideo && (
            <ToolbarButton
              label={words.toolbar.insertVideo}
              title={words.toolbar.insertVideoHint}
              onClick={() => setVideoOpen(true)}
              disabled={disabled}
            >
              <VideoIcon />
            </ToolbarButton>
          )}

          <input
            ref={filePicker}
            type="file"
            accept={ARTICLE_IMAGE_ACCEPT_ATTRIBUTE}
            multiple
            hidden
            onChange={onFilesPicked}
          />

          <span style={{ marginLeft: 'auto' }} />

          <div role="group" aria-label={words.toolbar.view}>
            {(['write', 'split', 'preview'] as const).map((option) => (
              <button
                key={option}
                type="button"
                className={`button button-small${mode === option ? ' button-primary' : ' button-quiet'}`}
                onClick={() => setMode(option)}
                aria-pressed={mode === option}
              >
                {words.toolbar[option]}
              </button>
            ))}
          </div>
        </div>

        {needsSaving && (
          <div className="notice notice-info" role="status">
            <div className="notice-body">
              <strong>{words.editor.saveFirstTitle(noun)}</strong>
              <p style={{ marginBottom: 0 }}>{words.editor.saveFirstBody(noun)}</p>
            </div>
            <div className="notice-actions">
              <button
                type="button"
                className="button button-quiet"
                onClick={() => setNeedsSaving(false)}
              >
                {t.common.dismiss}
              </button>
            </div>
          </div>
        )}

        {uploads.length > 0 && (
          <ul className="upload-list">
            {uploads.map((upload) => (
              <li key={upload.id} className={`upload upload-${upload.status}`}>
                <div className="upload-head">
                  <code>{upload.fileName}</code>
                  <span className="faint">{t.article.byteSize(upload.byteLength)}</span>
                </div>

                {upload.status === 'uploading' ? (
                  <div className="upload-progress">
                    <progress max={1} value={upload.progress} aria-label={words.uploads.uploading(upload.fileName)} />
                    <span className="faint" aria-live="polite">
                      {words.uploads.percent(Math.round(upload.progress * 100))}
                    </span>
                  </div>
                ) : (
                  <>
                    <p className="field-error" role="alert">
                      {upload.message}
                    </p>
                    <div className="button-row">
                      {upload.canReplace && upload.file && (
                        <button
                          type="button"
                          className="button button-small"
                          onClick={() => retryAsReplacement(upload)}
                        >
                          {words.editor.replaceStored}
                        </button>
                      )}
                      <button
                        type="button"
                        className="button button-small button-quiet"
                        onClick={() => dismissUpload(upload.id)}
                      >
                        {t.common.dismiss}
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {imageProblems > 0 && <ImageAuditNotice audit={audit} noun={noun} />}

        <div ref={layout} className={mode === 'split' ? 'editor-layout' : ''}>
          {mode !== 'preview' && (
            <div className="editor-pane">
              <label htmlFor="content" className="visually-hidden">
                {words.editor.bodyLabel(noun)}
              </label>
              <textarea
                id="content"
                ref={textarea}
                className={`editor-textarea${dragging ? ' editor-textarea-dragging' : ''}`}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={() => setDragging(false)}
                disabled={disabled}
                spellCheck
                placeholder={words.editor.placeholder(noun)}
              />
            </div>
          )}

          {mode !== 'write' && (
            <div className="editor-pane">
              {attachmentDownload.error && (
                <ErrorBanner error={attachmentDownload.error} onDismiss={attachmentDownload.clearError} />
              )}
              <MarkdownPreview
                content={value}
                linkTargets={linkTargets}
                buildImageSrc={buildImageSrc}
                videos={allowVideo}
                {...(allowAttachments
                  ? {
                      attachmentLinks,
                      buildAttachmentHref: adminAttachmentHref,
                      onLinkClick: handlePreviewLinkClick,
                    }
                  : {})}
              />
            </div>
          )}
        </div>

        {allowVideo && (
          <VideoInsertDialog
            open={videoOpen}
            onClose={() => setVideoOpen(false)}
            onInsert={insertAtCaret}
          />
        )}

        <ArticlePickerDialog
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onInsert={insertAtCaret}
          noun={noun}
          {...(pickerExclusion ? { excludeArticleId: pickerExclusion } : {})}
        />
      </>
    );
  },
);

/**
 * Puts the caret back after the editor has changed the text itself - a toolbar
 * button, a shortcut, the article picker, the video dialog, an image - and keeps
 * the textarea scrolled where the author had it.
 *
 * Replacing a textarea's value, which is what React does with the new `value`,
 * throws its scroll position away, and neither focus() nor setSelectionRange()
 * brings it back. The owner's report of 2026-11-04: "After inline inserts the
 * editor jumps to the bottom", seen for an external link and an article link.
 * Measured the same day in the browser pane's Chromium, with the text scrolled
 * to 1200 of 3100: after a toolbar link it stood at 0, after a link from the
 * article picker at 3100 - the browser following the caret the new value had
 * put at the end. So every caller reads `scrollTop` BEFORE it calls onChange,
 * and it is written back here, after the caret is placed, in the same frame
 * and so before anything is painted.
 *
 * A frame later rather than at once because the textarea does not hold the new
 * text until React has re-rendered, and after a dialog the focus is still
 * wherever the dialog left it.
 */
function restoreCaret(
  element: HTMLTextAreaElement,
  selectionStart: number,
  selectionEnd: number,
  scrollTop: number,
): void {
  requestAnimationFrame(() => {
    element.focus();
    element.setSelectionRange(selectionStart, selectionEnd);
    element.scrollTop = scrollTop;
  });
}

function ToolbarButton({
  label,
  title,
  onClick,
  children,
  disabled,
}: {
  /**
   * The accessible name. Keep it short - it is what a screen reader announces,
   * and every one of these buttons is announced by name alone now that most of
   * them show an icon rather than a word.
   */
  label: string;
  /**
   * A longer tooltip, when there is something worth adding that does not belong
   * in the name. Defaults to the label.
   */
  title?: string;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      /*
       * button-quiet is the load-bearing one: it clears the border and the
       * raised background that .button gives every other button in the app. The
       * three icon buttons were briefly written without it and read as though
       * they were pressed.
       */
      className="button button-small button-quiet"
      onClick={onClick}
      title={title ?? label}
      aria-label={label}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/**
 * What is wrong with the images in the text, as sentences the author can act on.
 *
 * Four lists rather than one, because the four call for four different pieces of
 * work: attach the file, correct the name, remove the reference, write the alt
 * text. Nothing here blocks a save - an article with a missing screenshot is still
 * worth publishing, and an author who is interrupted mid-edit should not have to
 * finish the images before they can park a draft.
 *
 * The empty-alt list is the one that earns its place. Alt text is an accessibility
 * requirement rather than a nicety, and it is the single easiest thing to leave
 * behind: the paste flow puts the caret between the brackets, and an author whose
 * next thought is the paragraph rather than the picture types straight past it.
 */
function ImageAuditNotice({
  audit,
  noun,
}: {
  audit: ArticleImageAudit;
  /** 'article' or 'release note', as the editor's own noun. */
  noun: string;
}): JSX.Element {
  const t = useMessages();
  const words = t.admin.editor.audit;
  const named = audit.missingAlt.filter((reference) => reference.target.length > 0);
  return (
    <div className="notice notice-warning" role="status">
      <div className="notice-body">
        <strong>{words.title(noun)}</strong>

        {audit.missing.length > 0 && (
          <p>
            {richText(words.notAttached, {
              names: audit.missing.map((fileName, index) => (
                <span key={fileName}>
                  {index > 0 && ', '}
                  <code>{fileName}</code>
                </span>
              )),
            })}
          </p>
        )}

        {audit.malformed.length > 0 && (
          <p>
            {richText(words.badName, {
              names: audit.malformed.map((reference, index) => (
                <span key={`${reference.index}-${reference.target}`}>
                  {index > 0 && ', '}
                  <code>{reference.target}</code>
                </span>
              )),
              requirement: t.admin.rules.imageFileNameRequirement,
            })}
          </p>
        )}

        {audit.unsafe.length > 0 && (
          <>
            <p>{words.notLoaded}</p>
            <ul>
              {audit.unsafe.map((reference) => (
                <li key={`${reference.index}-${reference.target}`}>
                  <code>{reference.target || words.empty}</code> —{' '}
                  {describeUnsafeReason(reference.unsafeReason, words)}
                </li>
              ))}
            </ul>
          </>
        )}

        {audit.missingAlt.length > 0 && (
          <p>
            {richText(words.missingAlt(audit.missingAlt.length, named.length > 0), {
              names: named.map((reference, index) => (
                <span key={`${reference.index}-${reference.target}`}>
                  {index > 0 && ', '}
                  <code>{reference.target}</code>
                </span>
              )),
            })}
          </p>
        )}
      </div>
    </div>
  );
}

/** Why a destination will never become a src, in terms an author can act on. */
function describeUnsafeReason(
  reason: ArticleImageUnsafeReason | undefined,
  words: EditorWords['audit'],
): string {
  switch (reason) {
    case 'empty':
      return words.unsafe.empty;
    case 'scheme':
      return words.unsafe.scheme;
    case 'protocol-relative':
      return words.unsafe.protocolRelative;
    case 'path':
      return words.unsafe.path;
    default:
      return words.unsafe.other;
  }
}

/*
 * The clipboard, read synchronously.
 *
 * DataTransferItemList is live only while the event is being handled, so the Files
 * are taken out here and everything asynchronous happens afterwards. An item with
 * kind 'file' and an image type is the only thing of interest; a screenshot on the
 * clipboard usually also carries an HTML and a text flavour, and taking the image
 * in preference to those is the whole point.
 */
function imageFilesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return [];

  const files: File[] = [];
  for (const item of Array.from(data.items)) {
    if (item.kind !== 'file') continue;
    if (!item.type.startsWith('image/')) continue;
    const file = item.getAsFile();
    if (file) files.push(file);
  }
  return files;
}

/**
 * The advisory version of the server's upload gate.
 *
 * Identical rules, from the same shared module, so the browser and the server
 * cannot disagree about what is acceptable - but the server remains the one that
 * decides. This exists to save a minute of upload on a file that was never going to
 * be accepted, and to name SVG explicitly while the author is still looking at what
 * they pasted.
 *
 * Only the first bytes are read. inspectArticleImageBytes measures whatever it is
 * handed, so passing the whole file would mean pulling 5 MB into memory to look at
 * twelve bytes; the size is taken from file.size instead, with the same wording the
 * shared module uses for it.
 */
async function inspectFileForUpload(
  file: File,
  t: Messages,
): Promise<{ ok: true; contentType: ArticleImageContentType } | { ok: false; message: string }> {
  const words = t.admin.editor.editor;
  if (file.size === 0) return { ok: false, message: words.fileEmpty };

  if (file.size > ARTICLE_IMAGE_MAX_BYTES) {
    return {
      ok: false,
      message: words.imageTooLarge(t.article.byteSize(file.size), t.admin.rules.imageSizeLimit),
    };
  }

  let header: Uint8Array;
  try {
    header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  } catch {
    return { ok: false, message: words.fileUnreadable };
  }

  const inspection = inspectArticleImageBytes(header);
  if (!inspection.ok || !inspection.contentType) {
    return { ok: false, message: inspection.message ?? words.notAnImage };
  }

  return { ok: true, contentType: inspection.contentType };
}

/**
 * Every browser hands a pasted image to the page as a File called "image.png".
 *
 * That name is legal, identical for every paste, and tells the author nothing, so it
 * is treated as no name at all and the readable pasted-<n> counter is used instead.
 * A file dropped from disk keeps its own name, put through the shared normaliser:
 * "Skaermbillede 2026-08-25 (1).PNG" becomes "skaermbillede-2026-08-25-1.png", which
 * the author will recognise in the markdown.
 *
 * A name that is already attached is used anyway rather than being made unique. The
 * server refuses it, and the refusal offers to replace the stored file - which is
 * almost always what dropping the same name twice meant.
 */
function chooseFileName(
  file: File,
  contentType: ArticleImageContentType,
  taken: ReadonlySet<string>,
): string {
  const normalised = file.name ? toArticleImageFileName(file.name, contentType) : null;
  if (normalised && !GENERIC_CLIPBOARD_NAMES.has(normalised)) return normalised;
  return nextPastedImageFileName(taken, contentType);
}

const GENERIC_CLIPBOARD_NAMES = new Set([
  'image.png',
  'image.jpg',
  'image.gif',
  'image.webp',
]);

/**
 * The sentence to show under a failed upload.
 *
 * The project's uniform error shape puts the specific message on the field that was
 * wrong, which for an upload is the bytes or the name they were given, so those are
 * preferred over the summary.
 */
function describeUploadFailure(error: unknown, words: EditorWords['editor']): string {
  if (error instanceof ApiError) {
    return error.fieldError('file') ?? error.fieldError('fileName') ?? error.message;
  }
  return error instanceof Error ? error.message : words.uploadFailed;
}

/**
 * Whether the refusal was "that name is taken".
 *
 * 409 is the only signal worth acting on. The name is half the primary key, so a
 * conflict on this route can only be a collision; the other 409 in this codebase -
 * a rowversion conflict - cannot happen here, because an image has no rowversion
 * and none is sent.
 */
function isNameTaken(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

